/**
 * Transport mode → carbon band, for coloring route segments on the map.
 *
 * A thin adapter over the app's single mode→emission-level rule,
 * `getEmissionLevel` + `EMISSION_LEVEL_COLOR` in shared/transport.ts, so the
 * map always agrees with the leg labels ("Low / Medium / High emissions") in
 * the journey timeline, leg sheet and compare dialog. Fixed per mode (not a
 * per-leg g/km calculation) so the map and its legend stay predictable:
 *  - very low: night train (a train that also saves a hotel night; Paulo
 *    2026-09-30), drawn in neon green so it stands out from the trains;
 *    bicycle and walking too, though the legend names only the night train
 *  - low:    train, bus
 *  - medium: car, car sharing, motorcycle, camper van, ferry/boat
 *  - high:   airplane
 * Unrecognized modes get no band (grey), mirroring the timeline, which shows
 * no emissions for them.
 */
import { EMISSION_LEVEL_COLOR, EmissionLevel, getEmissionLevel, isKnownTransportMode } from '../../shared/transport';

export type CarbonBand = EmissionLevel;

export function carbonBandForMode(mode: string | null | undefined): CarbonBand | null {
  return isKnownTransportMode(mode) ? getEmissionLevel(mode) : null;
}

/** Neon green for the night train's segments: the labels use a darker green (text on white). */
export const NIGHT_TRAIN_SEGMENT_COLOR = '#2ef27a';

export const CARBON_BAND_COLOR: Record<CarbonBand, string> = { ...EMISSION_LEVEL_COLOR, vlow: NIGHT_TRAIN_SEGMENT_COLOR };

/** Segments whose mode is missing or unrecognised. */
export const UNKNOWN_BAND_COLOR = '#8a948f';

export const CARBON_BAND_LEGEND: Array<{ band: CarbonBand; label: string; modes: string }> = [
  { band: 'vlow', label: 'Very low', modes: 'Night train' },
  { band: 'low', label: 'Low', modes: 'Train, bus' },
  { band: 'med', label: 'Medium', modes: 'Car, ferry' },
  { band: 'high', label: 'High', modes: 'Flight' },
];
